import React, { useEffect, useState } from "react"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import Chip from "@mui/material/Chip"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Paper from "@mui/material/Paper"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Typography from "@mui/material/Typography"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew"
import LoadingScreen from "components/common/LoadingScreen"
import { verifyResponse } from "utils/httpRequest"
import { getNpsQuestions, deleteNpsQuestion, toggleNpsQuestionStatus } from "services/npsQuestion"
import { UsePermissionVerification } from "components/customHooks"
import { PERMISSIONS } from "constants/permission"
import { useSnackbar } from "notistack"
import NpsQuestionDlg from "./NpsQuestionDlg"
import { NpsQuestionsWrapper } from "./NpsQuestions.style"

const NpsQuestions = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const [openNpsQuestionDialog, npsQuestionDialog, npsQuestionDialogLoading] =
    NpsQuestionDlg({
      onSubmit: () => {
        loadQuestions()
      },
    })

  const AccessControl = UsePermissionVerification()

  const loadQuestions = async () => {
    setLoading(true)
    try {
      const response = await getNpsQuestions()
      verifyResponse(response)
      setQuestions(response.data || [])
    } catch {
      enqueueSnackbar("Failed to load NPS questions.", { variant: "warning" })
      setQuestions([])
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async (question) => {
    try {
      const response = await toggleNpsQuestionStatus({ id: question.id })
      verifyResponse(response)
      await loadQuestions()
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to toggle NPS question status.",
        { variant: "warning" }
      )
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      const response = await deleteNpsQuestion({ id: deleteTarget.id })
      verifyResponse(response)
      setDeleteTarget(null)
      await loadQuestions()
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to delete NPS question.",
        { variant: "warning" }
      )
    }
  }

  useEffect(() => {
    loadQuestions()
  }, [])

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listNpsQuestion: AccessControl.VerifyAccess([
        PERMISSIONS.NPS_QUESTION.NPS_QUESTION_LIST,
      ]),
      addNpsQuestion: AccessControl.VerifyAccess([
        PERMISSIONS.NPS_QUESTION.NPS_QUESTION_ADD,
      ]),
      editNpsQuestion: AccessControl.VerifyAccess([
        PERMISSIONS.NPS_QUESTION.NPS_QUESTION_EDIT,
      ]),
      toggleStatusNpsQuestion: AccessControl.VerifyAccess([
        PERMISSIONS.NPS_QUESTION.NPS_QUESTION_TOGGLE_STATUS,
      ]),
      deleteNpsQuestion: AccessControl.VerifyAccess([
        PERMISSIONS.NPS_QUESTION.NPS_QUESTION_DELETE,
      ]),
    })
  }, [AccessControl.UserPermissions])

  return (
    <PageContent>
      <NpsQuestionsWrapper>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">NPS Questions</h1>
            <p className="page-subtitle">
              Manage the question wording members see in the NPS prompt, by tier and version.
            </p>
          </div>
          <div className="actions">
            {AccessControl.Access.addNpsQuestion && (
              <Button
                className="new-question-btn"
                variant="contained"
                size="medium"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={() => {
                  openNpsQuestionDialog()
                }}
              >
                Add Question
              </Button>
            )}
          </div>
        </PageHeading>
        <div className="page-surface">
          {AccessControl.Access?.listNpsQuestion && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Question</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Tiers</TableCell>
                    <TableCell>Min days</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questions.map((question) => (
                    <TableRow key={question.id} hover>
                      <TableCell className="question-text-cell">{question.questionText}</TableCell>
                      <TableCell>{question.versionLabel}</TableCell>
                      <TableCell>
                        {question.targetTiers && question.targetTiers.length > 0 ? (
                          question.targetTiers.map((tier) => (
                            <Chip key={tier} size="small" className="tier-chip" label={tier} />
                          ))
                        ) : (
                          <Chip size="small" className="tier-chip" label="All tiers" />
                        )}
                      </TableCell>
                      <TableCell>{question.minDaysSinceRegistration}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={question.isActive ? "Enabled" : "Disabled"}
                          className={`status-badge ${
                            question.isActive ? "status-active" : "status-inactive"
                          }`}
                        />
                      </TableCell>
                      <TableCell>
                        {AccessControl.Access.editNpsQuestion && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openNpsQuestionDialog(question)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {AccessControl.Access.toggleStatusNpsQuestion && (
                          <Tooltip title={question.isActive ? "Disable" : "Enable"}>
                            <IconButton size="small" onClick={() => handleToggleStatus(question)}>
                              <PowerSettingsNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {AccessControl.Access.deleteNpsQuestion && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(question)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!questions.length && (
                    <TableRow>
                      <TableCell className="table-empty" colSpan={6}>
                        No NPS questions configured yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!AccessControl.Access?.listNpsQuestion && (
            <div className="page-empty">
              You do not have permission to view NPS questions.
            </div>
          )}
        </div>
      </NpsQuestionsWrapper>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this NPS question ("{deleteTarget?.versionLabel}")?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingScreen loading={loading || npsQuestionDialogLoading} />
      {npsQuestionDialog}
    </PageContent>
  )
}

export default NpsQuestions
